// src/api/knowledgeApi.js
import { getApi } from './axiosInstance';

// Fetch all knowledge entries
export const fetchAllKnowledge = async () => {
    try {
        const response = await getApi().get('/Knowledge');
        return response.data;
    } catch (error) {
        // Check if this is a "Knowledge Not Found" response
        if (error.response?.status === 400 && error.response?.data?.message === 'Knowledge Not Found') {
            return []; // Return empty array for no knowledge
        }
        console.error('Error fetching knowledge:', error);
        throw error;
    }
};

// Fetch specific knowledge by ID
export const fetchKnowledgeById = async (id) => {
    try {
        const response = await getApi().get('Knowledge/GetKnowledge', {
            params: { knowledgeId: id },
        });
        return response.data;
    } catch (error) {
        console.error(`Error fetching knowledge with ID ${id}:`, error);
        throw error;
    }
};

// Fetch knowledge based on keywords
export const fetchKnowledgeByKeywords = async (selectedKeywords) => {
    try {
        // Format the keywords according to the API's expected schema
        const keywords = selectedKeywords.map(kw => ({
            id: kw.id || 0,
            keywordText: kw.keywordText,
            userIdentifier: kw.userIdentifier || 'string',
            userName: kw.userName || 'string',
            creationDate: kw.creationDate || new Date().toISOString()
        }));

        console.log('Sending keywords to API:', keywords);

        // Send the request with the keywords array directly as per Swagger spec
        const response = await getApi().post('/Knowledge/GetKnowledgeFromKeywords', keywords);
        return response.data;
    } catch (error) {
        if (error.response) {
            console.error('API Error Response:', {
                status: error.response.status,
                data: error.response.data,
                headers: error.response.headers
            });
        }
        console.error('Error fetching knowledge by keywords:', error);
        throw error;
    }
};

// Fetch all keywords
export const fetchAllKeywords = async () => {
    try {
        const response = await getApi().get('/Knowledge/GetKeywords');
        return response.data;
    } catch (error) {
        console.error('Error fetching keywords:', error);
        throw error;
    }
};

// Create a new knowledge entry
export const createKnowledge = async (knowledgeData) => {
    try {
        // Format the knowledge data according to the API's expected schema
        const formattedData = {
            id: 0, // API expects id to be 0 for new entries
            knowledgeText: knowledgeData.knowledgeText,
            knowledgeSummary: knowledgeData.knowledgeSummary,
            userIdentifier: knowledgeData.userIdentifier || 'string',
            userName: knowledgeData.userName || 'string',
            creationDate: knowledgeData.creationDate || new Date().toISOString(),
            classifiers: knowledgeData.classifiers?.map(cls => ({
                id: 0, // API expects id to be 0 for new classifiers
                classText: cls.classText,
                userIdentifier: cls.userIdentifier || 'string',
                userName: cls.userName || 'string',
                creationDate: cls.creationDate || new Date().toISOString()
            })) || [],
            keywords: [] // API expects keywords array even if empty
        };

        console.log('Sending knowledge data to API:', formattedData);

        const response = await getApi().post('/Knowledge', formattedData);
        console.log('API Response:', {
            status: response.status,
            statusText: response.statusText,
            data: response.data,
            headers: response.headers
        });
        return response.data;
    } catch (error) {
        if (error.response) {
            console.error('API Error Response:', {
                status: error.response.status,
                statusText: error.response.statusText,
                data: error.response.data,
                headers: error.response.headers
            });
        } else if (error.request) {
            console.error('No response received:', error.request);
        } else {
            console.error('Error setting up request:', error.message);
        }
        console.error('Error creating knowledge:', error);
        throw error;
    }
};

// Delete a knowledge entry by ID
export const deleteKnowledge = async (id) => {
    try {
        await getApi().delete('/Knowledge', {
            params: { Id: id }
        });
    } catch (error) {
        console.error(`Error deleting knowledge with ID ${id}:`, error);
        throw error;
    }
};

// Fetch all classifiers
export const fetchAllClassifiers = async () => {
    try {
        const response = await getApi().get('/Knowledge/GetClassifiers');
        return response.data;
    } catch (error) {
        console.error('Error fetching classifiers:', error);
        throw error;
    }
};

// Fetch knowledge based on classifiers
export const fetchKnowledgeByClassifiers = async (classifiers) => {
    try {
        // Format the classifiers according to the API's expected schema
        const formattedClassifiers = classifiers.map(cls => ({
            id: cls.id || 0,
            classText: cls.classText,
            userIdentifier: cls.userIdentifier || 'string',
            userName: cls.userName || 'string',
            creationDate: cls.creationDate || new Date().toISOString()
        }));

        console.log('Sending classifiers to API:', formattedClassifiers);

        // Send the request with the classifiers array directly as per Swagger spec
        const response = await getApi().post('/Knowledge/GetKnowledgeFromClassifiers', formattedClassifiers);
        return response.data;
    } catch (error) {
        if (error.response) {
            console.error('API Error Response:', {
                status: error.response.status,
                data: error.response.data,
                headers: error.response.headers
            });
        }
        console.error('Error fetching knowledge by classifiers:', error);
        throw error;
    }
};
